import React from "react";
import {
  createBrowserRouter,
  Outlet,
  Navigate,
  useSearchParams,
} from "react-router-dom";

// Contexts and Providers
import { AuthProvider, useAuth } from "../contexts/AuthProvider";
import {
  OnboardingProvider,
  useOnboarding,
} from "../contexts/OnboardingContext";
import { SocketProvider } from "../contexts/SocketContext";
import { NotificationProvider } from "../contexts/NotificationContext";
import { FriendsProvider } from "../contexts/FriendsContext";
import { StripeProvider } from "../contexts/StripeProvider";
import GoogleMapsProvider from "../components/GoogleMapsProvider";
import { TooltipProvider } from "../components/SmartTooltip";
import { PaymentErrorBoundary } from "../components/payment/PaymentErrorBoundary";
import { UnifiedOnboardingWizard } from "../components/onboarding/UnifiedOnboardingWizard";
import {
  ProtectedRoute,
  AdminRoute,
} from "../components/routing/ProtectedRoute";

// Layouts
import { PublicLayout, SimpleLayout } from "../components/layouts";
import { AppLayout } from "../components/AppLayout";

// Non-lazy loaded pages (critical path)
import Homepage from "../pages/HomePage";

// Lazy loaded pages
import {
  Dashboard,
  Meetups,
  CreateMeetup,
  CreateMeetupWizard,
  MeetupDetails,
  MeetupDetailEnhanced,
  EditMeetup,
  Profile,
  Friends,
  FindFriends,
  PublicProfile,
  Groups,
  Invitations,
  PaymentPage,
  HowItWorks,
  Features,
  Pricing,
  UseCases,
  Accessibility,
  About,
  Contact,
  LoginPage,
  RegisterPage,
  ForgotPassword,
  PasswordReset,
  AuthCallback,
  InvitationPage,
  NotFoundPage,
  SentryTestPage,
  PushNotificationTest,
  NotificationSettings,
  GuestInvitationWizard,
  Blog,
  OAuthTest,
  MapTest,
  Privacy,
  Terms,
  DataDeletion,
  Unsubscribe,
  HelpCenter,
  MobileDemo,
  MeetupDetailsMobile,
  Templates,
  Account,
  SubscriptionManagement,
  Security,
  UserAnalytics,
  AdminDashboard,
  AdminAnalyticsMetrics,
  AdminAnalyticsRevenue,
  AdminAnalyticsChurn,
  AdminAnalyticsUsage,
  AdminDataUsers,
  AdminDataMeetups,
  AdminUsersSearch,
  AdminSystemHealth,
  UserList,
  UserDetail,
  UserActivity,
  PendingMeetups,
  MeetupDetail,
  MeetupEdit,
  ReportedMeetups,
  ReportDetail,
  SystemLogs,
  ErrorDashboard,
  PerformanceMetrics,
  DatabaseStatus,
  ContentReports,
  ContentReportDetail,
  FlaggedUsers,
  SystemConfiguration,
  DensityEngine,
  Referrals,
  SavedSpotsPage,
} from "./lazyPages";

// Redirect /beta?code=XXX to /register?code=XXX
const BetaCodeRedirect: React.FC = () => {
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const targetUrl = code ? `/register?code=${code}` : "/register";
  return <Navigate to={targetUrl} replace />;
};

// Wrapper component with AuthProvider inside router context
// GoogleMapsProvider is NOT loaded globally for better performance
// It's only loaded on routes that actually need maps (dashboard, meetups, etc.)
const AppWrapper: React.FC = () => {
  return (
    <AuthProvider>
      <OnboardingProvider>
        <AppWrapperContent />
      </OnboardingProvider>
    </AuthProvider>
  );
};

// Wrapper for routes that need Google Maps
const WithMaps: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <GoogleMapsProvider>{children}</GoogleMapsProvider>
);

// Inner component to access onboarding context
const AppWrapperContent: React.FC = () => {
  const {
    onboardingMode,
    completeUnifiedOnboarding,
    skipUnifiedOnboarding,
    isCompleted: _isCompleted,
  } = useOnboarding();
  const { user: _user } = useAuth();

  return (
    <TooltipProvider>
      {/* Unified Onboarding Wizard - handles profile setup + tour in one flow */}
      {onboardingMode !== "none" && (
        <UnifiedOnboardingWizard
          mode={onboardingMode}
          onComplete={completeUnifiedOnboarding}
          onSkip={skipUnifiedOnboarding}
        />
      )}
      <Outlet />
    </TooltipProvider>
  );
};

// Create router with future flags to fix React Router warnings
export const router = createBrowserRouter(
  [
    {
      path: "/oauth-test",
      element: (
        <SimpleLayout>
          <OAuthTest />
        </SimpleLayout>
      ),
    },
    {
      path: "/",
      element: <AppWrapper />,
      children: [
        // Public routes
        {
          index: true,
          element: (
            <PublicLayout fullWidth={true}>
              <Homepage />
            </PublicLayout>
          ),
        },
        {
          path: "invite/:token",
          element: (
            <PublicLayout>
              <InvitationPage />
            </PublicLayout>
          ),
        },
        {
          path: "invitation/:token",
          element: (
            <WithMaps>
              <PublicLayout fullWidth={true}>
                <GuestInvitationWizard />
              </PublicLayout>
            </WithMaps>
          ),
        },
        {
          path: "guest/invitation/:token",
          element: (
            <WithMaps>
              <PublicLayout fullWidth={true}>
                <GuestInvitationWizard />
              </PublicLayout>
            </WithMaps>
          ),
        },
        {
          path: "login",
          element: (
            <PublicLayout>
              <LoginPage />
            </PublicLayout>
          ),
        },
        {
          path: "register",
          element: (
            <PublicLayout>
              <RegisterPage />
            </PublicLayout>
          ),
        },
        {
          path: "forgot-password",
          element: (
            <PublicLayout>
              <ForgotPassword />
            </PublicLayout>
          ),
        },
        {
          path: "reset-password",
          element: (
            <PublicLayout>
              <PasswordReset />
            </PublicLayout>
          ),
        },
        {
          path: "beta",
          element: <BetaCodeRedirect />,
        },
        {
          path: "auth/callback",
          element: (
            <PublicLayout>
              <AuthCallback />
            </PublicLayout>
          ),
        },
        {
          path: "how-it-works",
          element: (
            <PublicLayout fullWidth={true}>
              <HowItWorks />
            </PublicLayout>
          ),
        },
        {
          path: "features",
          element: (
            <PublicLayout fullWidth={true}>
              <Features />
            </PublicLayout>
          ),
        },
        {
          path: "pricing",
          element: (
            <PublicLayout fullWidth={true}>
              <Pricing />
            </PublicLayout>
          ),
        },
        {
          path: "use-cases",
          element: (
            <PublicLayout fullWidth={true}>
              <UseCases />
            </PublicLayout>
          ),
        },
        {
          path: "about",
          element: (
            <PublicLayout fullWidth={true}>
              <About />
            </PublicLayout>
          ),
        },
        {
          path: "contact",
          element: (
            <PublicLayout fullWidth={true}>
              <Contact />
            </PublicLayout>
          ),
        },
        {
          path: "blog",
          element: (
            <PublicLayout fullWidth={true}>
              <Blog />
            </PublicLayout>
          ),
        },
        {
          path: "privacy",
          element: (
            <PublicLayout fullWidth={true}>
              <Privacy />
            </PublicLayout>
          ),
        },
        {
          path: "terms",
          element: (
            <PublicLayout fullWidth={true}>
              <Terms />
            </PublicLayout>
          ),
        },
        {
          path: "accessibility",
          element: (
            <PublicLayout fullWidth={true}>
              <Accessibility />
            </PublicLayout>
          ),
        },
        {
          path: "data-deletion",
          element: (
            <PublicLayout fullWidth={true}>
              <DataDeletion />
            </PublicLayout>
          ),
        },
        {
          path: "unsubscribe",
          element: (
            <PublicLayout fullWidth={true}>
              <Unsubscribe />
            </PublicLayout>
          ),
        },

        // Protected routes (with maps for location-based features)
        {
          path: "dashboard",
          element: (
            <ProtectedRoute>
              <WithMaps>
                <SocketProvider>
                  <AppLayout>
                    <Dashboard />
                  </AppLayout>
                </SocketProvider>
              </WithMaps>
            </ProtectedRoute>
          ),
        },
        {
          path: "mobile-demo",
          element: (
            <ProtectedRoute>
              <MobileDemo />
            </ProtectedRoute>
          ),
        },
        {
          path: "payment",
          element: (
            <ProtectedRoute>
              <StripeProvider>
                <PaymentErrorBoundary>
                  <AppLayout>
                    <PaymentPage />
                  </AppLayout>
                </PaymentErrorBoundary>
              </StripeProvider>
            </ProtectedRoute>
          ),
        },
        {
          path: "meetups",
          element: (
            <ProtectedRoute>
              <WithMaps>
                <SocketProvider>
                  <AppLayout>
                    <Meetups />
                  </AppLayout>
                </SocketProvider>
              </WithMaps>
            </ProtectedRoute>
          ),
        },
        {
          path: "meetups/new",
          element: (
            <ProtectedRoute>
              <WithMaps>
                <SocketProvider>
                  <AppLayout>
                    <CreateMeetup />
                  </AppLayout>
                </SocketProvider>
              </WithMaps>
            </ProtectedRoute>
          ),
        },
        {
          path: "meetups/create",
          element: (
            <ProtectedRoute>
              <WithMaps>
                <SocketProvider>
                  <AppLayout>
                    <CreateMeetupWizard />
                  </AppLayout>
                </SocketProvider>
              </WithMaps>
            </ProtectedRoute>
          ),
        },
        {
          path: "meetups/new/wizard",
          element: (
            <ProtectedRoute>
              <WithMaps>
                <SocketProvider>
                  <AppLayout>
                    <CreateMeetupWizard />
                  </AppLayout>
                </SocketProvider>
              </WithMaps>
            </ProtectedRoute>
          ),
        },
        {
          path: "meetups/:id",
          element: (
            <ProtectedRoute>
              <WithMaps>
                <SocketProvider>
                  <AppLayout>
                    <MeetupDetails />
                  </AppLayout>
                </SocketProvider>
              </WithMaps>
            </ProtectedRoute>
          ),
        },
        {
          path: "meetups/:id/mobile",
          element: (
            <ProtectedRoute>
              <WithMaps>
                <SocketProvider>
                  <MeetupDetailsMobile />
                </SocketProvider>
              </WithMaps>
            </ProtectedRoute>
          ),
        },
        {
          path: "meetups/:id/enhanced",
          element: (
            <ProtectedRoute>
              <WithMaps>
                <SocketProvider>
                  <MeetupDetailEnhanced />
                </SocketProvider>
              </WithMaps>
            </ProtectedRoute>
          ),
        },
        {
          path: "meetups/:id/edit",
          element: (
            <ProtectedRoute>
              <WithMaps>
                <SocketProvider>
                  <AppLayout>
                    <EditMeetup />
                  </AppLayout>
                </SocketProvider>
              </WithMaps>
            </ProtectedRoute>
          ),
        },
        {
          path: "profile",
          element: (
            <ProtectedRoute>
              <WithMaps>
                <SocketProvider>
                  <AppLayout>
                    <Profile />
                  </AppLayout>
                </SocketProvider>
              </WithMaps>
            </ProtectedRoute>
          ),
        },
        {
          path: "friends",
          element: (
            <ProtectedRoute>
              <SocketProvider>
                <NotificationProvider>
                  <FriendsProvider>
                    <AppLayout>
                      <Friends />
                    </AppLayout>
                  </FriendsProvider>
                </NotificationProvider>
              </SocketProvider>
            </ProtectedRoute>
          ),
        },
        {
          path: "find-friends",
          element: (
            <ProtectedRoute>
              <SocketProvider>
                <AppLayout>
                  <FindFriends />
                </AppLayout>
              </SocketProvider>
            </ProtectedRoute>
          ),
        },
        {
          path: "users/:userId",
          element: (
            <ProtectedRoute>
              <SocketProvider>
                <AppLayout>
                  <PublicProfile />
                </AppLayout>
              </SocketProvider>
            </ProtectedRoute>
          ),
        },
        {
          path: "groups",
          element: (
            <ProtectedRoute>
              <SocketProvider>
                <NotificationProvider>
                  <FriendsProvider>
                    <AppLayout>
                      <Groups />
                    </AppLayout>
                  </FriendsProvider>
                </NotificationProvider>
              </SocketProvider>
            </ProtectedRoute>
          ),
        },
        {
          path: "invitations",
          element: (
            <ProtectedRoute>
              <SocketProvider>
                <NotificationProvider>
                  <AppLayout>
                    <Invitations />
                  </AppLayout>
                </NotificationProvider>
              </SocketProvider>
            </ProtectedRoute>
          ),
        },
        {
          path: "referrals",
          element: (
            <ProtectedRoute>
              <AppLayout>
                <Referrals />
              </AppLayout>
            </ProtectedRoute>
          ),
        },
        {
          path: "saved-spots",
          element: (
            <ProtectedRoute>
              <AppLayout>
                <SavedSpotsPage />
              </AppLayout>
            </ProtectedRoute>
          ),
        },
        {
          path: "sentry-test",
          element: (
            <ProtectedRoute>
              <AppLayout>
                <SentryTestPage />
              </AppLayout>
            </ProtectedRoute>
          ),
        },
        {
          path: "map-test",
          element: (
            <WithMaps>
              <MapTest />
            </WithMaps>
          ),
        },
        {
          path: "push-test",
          element: (
            <ProtectedRoute>
              <AppLayout>
                <PushNotificationTest />
              </AppLayout>
            </ProtectedRoute>
          ),
        },
        {
          path: "notifications",
          element: (
            <ProtectedRoute>
              <AppLayout>
                <NotificationSettings />
              </AppLayout>
            </ProtectedRoute>
          ),
        },

        // Templates route
        {
          path: "templates",
          element: (
            <ProtectedRoute>
              <AppLayout>
                <Templates />
              </AppLayout>
            </ProtectedRoute>
          ),
        },

        // Account routes
        {
          path: "account",
          element: (
            <ProtectedRoute>
              <AppLayout>
                <Account />
              </AppLayout>
            </ProtectedRoute>
          ),
        },
        {
          path: "account/subscription",
          element: (
            <ProtectedRoute>
              <StripeProvider>
                <PaymentErrorBoundary>
                  <AppLayout>
                    <SubscriptionManagement />
                  </AppLayout>
                </PaymentErrorBoundary>
              </StripeProvider>
            </ProtectedRoute>
          ),
        },
        {
          path: "account/security",
          element: (
            <ProtectedRoute>
              <AppLayout>
                <Security />
              </AppLayout>
            </ProtectedRoute>
          ),
        },
        {
          path: "account/analytics",
          element: (
            <ProtectedRoute>
              <AppLayout>
                <UserAnalytics />
              </AppLayout>
            </ProtectedRoute>
          ),
        },
        {
          path: "account/notifications",
          element: (
            <ProtectedRoute>
              <AppLayout>
                <NotificationSettings />
              </AppLayout>
            </ProtectedRoute>
          ),
        },
        {
          path: "help",
          element: (
            <ProtectedRoute>
              <AppLayout>
                <HelpCenter />
              </AppLayout>
            </ProtectedRoute>
          ),
        },

        // Admin routes
        {
          path: "admin",
          element: (
            <AdminRoute>
              <AppLayout>
                <AdminDashboard />
              </AppLayout>
            </AdminRoute>
          ),
        },
        // Admin User Management - Phase 1
        {
          path: "admin/users",
          element: (
            <AdminRoute>
              <AppLayout>
                <UserList />
              </AppLayout>
            </AdminRoute>
          ),
        },
        {
          path: "admin/users/:userId",
          element: (
            <AdminRoute>
              <AppLayout>
                <UserDetail />
              </AppLayout>
            </AdminRoute>
          ),
        },
        {
          path: "admin/users/:userId/activity",
          element: (
            <AdminRoute>
              <AppLayout>
                <UserActivity />
              </AppLayout>
            </AdminRoute>
          ),
        },
        // Admin Meetup Moderation - Phase 2
        {
          path: "admin/meetups/pending",
          element: (
            <AdminRoute>
              <AppLayout>
                <PendingMeetups />
              </AppLayout>
            </AdminRoute>
          ),
        },
        {
          path: "admin/meetups/:id",
          element: (
            <AdminRoute>
              <AppLayout>
                <MeetupDetail />
              </AppLayout>
            </AdminRoute>
          ),
        },
        {
          path: "admin/meetups/:id/edit",
          element: (
            <AdminRoute>
              <AppLayout>
                <MeetupEdit />
              </AppLayout>
            </AdminRoute>
          ),
        },
        {
          path: "admin/meetups/reported",
          element: (
            <AdminRoute>
              <AppLayout>
                <ReportedMeetups />
              </AppLayout>
            </AdminRoute>
          ),
        },
        {
          path: "admin/reports/:id",
          element: (
            <AdminRoute>
              <AppLayout>
                <ReportDetail />
              </AppLayout>
            </AdminRoute>
          ),
        },
        // Admin System Diagnostics - Phase 3
        {
          path: "admin/diagnostics/logs",
          element: (
            <AdminRoute>
              <AppLayout>
                <SystemLogs />
              </AppLayout>
            </AdminRoute>
          ),
        },
        {
          path: "admin/diagnostics/errors",
          element: (
            <AdminRoute>
              <AppLayout>
                <ErrorDashboard />
              </AppLayout>
            </AdminRoute>
          ),
        },
        {
          path: "admin/diagnostics/performance",
          element: (
            <AdminRoute>
              <AppLayout>
                <PerformanceMetrics />
              </AppLayout>
            </AdminRoute>
          ),
        },
        {
          path: "admin/diagnostics/database",
          element: (
            <AdminRoute>
              <AppLayout>
                <DatabaseStatus />
              </AppLayout>
            </AdminRoute>
          ),
        },
        // Admin Content Moderation - Phase 5
        {
          path: "admin/moderation/reports",
          element: (
            <AdminRoute>
              <AppLayout>
                <ContentReports />
              </AppLayout>
            </AdminRoute>
          ),
        },
        {
          path: "admin/moderation/reports/:id",
          element: (
            <AdminRoute>
              <AppLayout>
                <ContentReportDetail />
              </AppLayout>
            </AdminRoute>
          ),
        },
        {
          path: "admin/moderation/flagged-users",
          element: (
            <AdminRoute>
              <AppLayout>
                <FlaggedUsers />
              </AppLayout>
            </AdminRoute>
          ),
        },
        // Admin System Configuration - Phase 6
        {
          path: "admin/config",
          element: (
            <AdminRoute>
              <AppLayout>
                <SystemConfiguration />
              </AppLayout>
            </AdminRoute>
          ),
        },
        // Admin Density Engine - Phase 8
        {
          path: "admin/density-engine",
          element: (
            <AdminRoute>
              <AppLayout>
                <DensityEngine />
              </AppLayout>
            </AdminRoute>
          ),
        },
        {
          path: "admin/analytics/metrics",
          element: (
            <AdminRoute>
              <AppLayout>
                <AdminAnalyticsMetrics />
              </AppLayout>
            </AdminRoute>
          ),
        },
        {
          path: "admin/analytics/revenue",
          element: (
            <AdminRoute>
              <AppLayout>
                <AdminAnalyticsRevenue />
              </AppLayout>
            </AdminRoute>
          ),
        },
        {
          path: "admin/analytics/churn",
          element: (
            <AdminRoute>
              <AppLayout>
                <AdminAnalyticsChurn />
              </AppLayout>
            </AdminRoute>
          ),
        },
        {
          path: "admin/analytics/usage",
          element: (
            <AdminRoute>
              <AppLayout>
                <AdminAnalyticsUsage />
              </AppLayout>
            </AdminRoute>
          ),
        },
        {
          path: "admin/data/users",
          element: (
            <AdminRoute>
              <AppLayout>
                <AdminDataUsers />
              </AppLayout>
            </AdminRoute>
          ),
        },
        {
          path: "admin/data/meetups",
          element: (
            <AdminRoute>
              <AppLayout>
                <AdminDataMeetups />
              </AppLayout>
            </AdminRoute>
          ),
        },
        {
          path: "admin/users/search",
          element: (
            <AdminRoute>
              <AppLayout>
                <AdminUsersSearch />
              </AppLayout>
            </AdminRoute>
          ),
        },
        {
          path: "admin/health/system",
          element: (
            <AdminRoute>
              <AppLayout>
                <AdminSystemHealth />
              </AppLayout>
            </AdminRoute>
          ),
        },

        // Fallback 404
        {
          path: "*",
          element: (
            <PublicLayout>
              <NotFoundPage />
            </PublicLayout>
          ),
        },
      ],
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
    },
  },
);

export default router;
